import { useSelector } from 'react-redux';
import { useChatbotCRUDActions } from '@mm/state/actions/ChatbotCRUDActions';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import {
  activeChatbotIdSelector,
  chatbotIsActiveOnConnectedPageSelector,
  connectedPageSelector,
} from '@mm/state/selectors/viewSelectors';

export const useInactiveBotActivator = () => {
  const isActiveChatbot = useSelector(chatbotIsActiveOnConnectedPageSelector);
  const connectedPage = useSelector(connectedPageSelector);
  const { attachToFacebookPage } = useChatbotCRUDActions();
  const { openActivateInactiveBotModal } = useModalActions();
  const chatbotId = useSelector(activeChatbotIdSelector);

  const guardBotSwitchModal = async (
    onBotActive?: () => void,
    onReject?: () => void,
  ) => {
    if (!connectedPage) {
      onReject?.();
      return;
    }

    if (!connectedPage?.activeBotId) {
      await attachToFacebookPage(chatbotId, connectedPage.id);
      onBotActive?.();
      return;
    }

    openActivateInactiveBotModal({
      switchToAnotherBot: true,
      onBotActivateConfirm: onBotActive,
      onClose: onReject,
    });
  };

  const activateChatbotWithCallback = (onBotActivateConfirm: () => void) => {
    if (!isActiveChatbot) {
      openActivateInactiveBotModal({
        onBotActivateConfirm: () =>
          setTimeout(() => guardBotSwitchModal(onBotActivateConfirm), 500),
        useCustomOnBotActivateConfirm: true,
      });
    } else {
      onBotActivateConfirm();
    }
  };

  const activateBotGuard = () =>
    new Promise((resolve: (value?: unknown) => void, reject) => {
      if (isActiveChatbot) {
        resolve();
        return;
      }

      openActivateInactiveBotModal({
        // note delay is needed as modal system requires 300ms to close
        // while onClickActive might trigger reopen of the same modal
        // thus reducer doesn't identify that it shouldn't be closed
        // would be great to avoid such scenarios and
        // generalize work with modal interface to promises
        onBotActivateConfirm: () => {
          setTimeout(() => guardBotSwitchModal(resolve, reject), 500);
        },
        onClose: reject,
        useCustomOnBotActivateConfirm: true,
      });
    });

  return {
    isActiveChatbot,
    onClickActivate: guardBotSwitchModal,
    activateChatbotWithCallback,
    activateBotGuard,
  };
};
